import type { FC, ReactNode } from 'react'
import type { ListingSummary } from '@/types'

import Link from 'next/link'
import { useRef } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { ListingCard } from '@/components/homes/listing-card'

interface ListingRowProps {
    id: string
    title: ReactNode
    subtitle?: ReactNode
    listings: ListingSummary[]
    href?: string
    linkLabel?: string
}

/* A horizontally scrolling row of homes with arrow buttons on larger screens. */
export const ListingRow: FC<ListingRowProps> = ({ id, title, subtitle, listings, href, linkLabel = 'Show all' }): ReactNode => {
    const scrollerRef = useRef<HTMLUListElement>(null)

    const scroll = (direction: 1 | -1) => {
        const scroller = scrollerRef.current

        if (scroller) scroller.scrollBy({ left: direction * scroller.clientWidth * 0.9, behavior: 'smooth' })
    }

    if (!listings.length) return null

    return (
        <section id={id} aria-labelledby={`${id}-title`} className='py-8'>
            <div className='mb-4 flex items-end justify-between gap-4'>
                <div className='min-w-0'>
                    <h2 id={`${id}-title`} className='font-heading text-xl font-semibold tracking-tight sm:text-2xl'>{title}</h2>
                    {subtitle && <p className='mt-1 text-sm text-muted-foreground sm:text-base'>{subtitle}</p>}
                </div>

                <div className='flex shrink-0 items-center gap-2'>
                    {href && (
                        <Link href={href} className='rounded-lg px-2 py-1 text-sm font-semibold underline underline-offset-4 hover:bg-muted'>
                            {linkLabel}
                        </Link>
                    )}

                    <button
                        type='button'
                        onClick={() => scroll(-1)}
                        aria-label='Scroll left'
                        className='hidden size-8 items-center justify-center rounded-full border border-border bg-white transition hover:shadow-pill sm:flex'
                    >
                        <ChevronLeft className='size-4' aria-hidden='true' />
                    </button>

                    <button
                        type='button'
                        onClick={() => scroll(1)}
                        aria-label='Scroll right'
                        className='hidden size-8 items-center justify-center rounded-full border border-border bg-white transition hover:shadow-pill sm:flex'
                    >
                        <ChevronRight className='size-4' aria-hidden='true' />
                    </button>
                </div>
            </div>

            <ul
                ref={scrollerRef}
                className='no-scrollbar -mx-4 flex snap-x snap-mandatory scroll-px-4 gap-4 overflow-x-auto px-4 pb-2 sm:-mx-6 sm:scroll-px-6 sm:gap-6 sm:px-6 lg:mx-0 lg:scroll-px-0 lg:px-0'
            >
                {listings.map(listing => (
                    <li key={listing.id} className='w-[78%] shrink-0 snap-start sm:w-[45%] lg:w-[calc((100%-4.5rem)/4)] 2xl:w-[calc((100%-6rem)/5)]'>
                        <ListingCard listing={listing} sizes='(min-width: 1536px) 18vw, (min-width: 1024px) 23vw, (min-width: 640px) 45vw, 78vw' />
                    </li>
                ))}
            </ul>
        </section>
    )
}
